import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import MaterialCard from "../components/MaterialCard";
import { groupByFolder, mixItems, MixedGrid } from "../components/MaterialFolderGrid";
import SearchBar from "../components/SearchBar";

export default function Home() {
  const [data, setData] = useState({ products: [], featured: [], recent: [] });
  const [query, setQuery] = useState("");
  const [company, setCompany] = useState({ folders: [], materials: [] });
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/home").then((r) => setData(r.data)),
      Promise.all([
        api.get("/folders", { params: { company: true } }),
        api.get("/materials", { params: { company: true } }),
      ]).then(([f, m]) => setCompany({ folders: f.data || [], materials: m.data || [] })),
    ]).finally(() => setLoading(false));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if ((query || "").trim()) {
      // event logged on destination page too; log here to attribute source
    }
    nav(`/cerca${query ? `?q=${encodeURIComponent(query)}` : ""}`);
  };

  return (
    <div className="space-y-8">
      <section>
        <h1 className="text-2xl font-bold tracking-tight text-[#1F2937] sm:text-3xl">
          Benvenuto in Maven Academy
        </h1>
        <p className="mt-1 text-sm text-[#6B7280]">
          Cerca un prodotto, apri il materiale, chiudi la visita.
        </p>
        <form onSubmit={onSearch} className="mt-4">
          <SearchBar
            size="lg"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca prodotto o materiale..."
            testId="global-search-input"
          />
        </form>
      </section>

      <section data-testid="home-azienda-section">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#1F2937] sm:text-xl">Azienda</h2>
          <Link to="/azienda" className="text-xs font-semibold text-[#0153A3]" data-testid="home-see-all-azienda">
            Vedi tutti
          </Link>
        </div>
        {loading ? <SkeletonList /> : <AziendaPreview {...company} />}
      </section>

      <section data-testid="home-materiali-evidenza-section">
        <h2 className="mb-3 text-lg font-semibold text-[#1F2937] sm:text-xl">
          Materiali in evidenza
        </h2>
        {loading ? (
          <SkeletonList />
        ) : data.featured.length === 0 ? (
          <EmptyState text="Nessun materiale in evidenza per ora." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.featured.map((m, i) => (
              <MaterialCard key={m.id} m={m} index={i} />
            ))}
          </div>
        )}
      </section>

      <section data-testid="home-aggiunti-recente-section">
        <h2 className="mb-3 text-lg font-semibold text-[#1F2937] sm:text-xl">Aggiunti di recente</h2>
        {loading ? (
          <SkeletonList />
        ) : data.recent.length === 0 ? (
          <EmptyState text="Non ci sono ancora materiali." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.recent.map((m, i) => (
              <MaterialCard key={m.id} m={m} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function AziendaPreview({ folders, materials }) {
  const byFolder = groupByFolder(materials);
  const items = mixItems(folders, materials, byFolder).slice(0, 8);
  if (items.length === 0) return <EmptyState text="Nessun contenuto aziendale disponibile." />;
  return (
    <MixedGrid
      items={items}
      byFolder={byFolder}
      folderLink={(f) => `/azienda?cartella=${f.id}`}
      testId="home-azienda-grid"
    />
  );
}
function SkeletonList() {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="maven-card h-24 animate-pulse" />
      ))}
    </div>
  );
}
function EmptyState({ text }) {
  return (
    <div className="rounded-xl border border-dashed border-[#E5E7EB] bg-[#F7F9FC] p-6 text-center text-sm text-[#6B7280]">
      {text}
    </div>
  );
}
